'use client';

import { Fragment, type ReactNode } from 'react';

import { MIN_QUERY, useHeaderSearch } from '@eliadmin/core/src/components/nt/header/header-search';
import { useResultTexts } from '@eliadmin/core/src/components/nt/header/search/search-results';
import { GenericError } from '@eliadmin/core/src/components/error/generic-error';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { SearchEmpty } from './search-empty';
import { SearchListing } from './search-listing';
import { ProductsSkeleton, SearchSection } from './search-skeleton';
import { SearchSuggestions } from './search-suggestions';
import type { SearchViewProps } from './search-types';
import { useSearchProducts } from './use-search-products';

/**
 * H1 of a search with a query: the page field „Výsledky pre „{dotaz}““ with the query in brand green
 * (text, never HTML – the query comes from the URL); without the field the core pair
 * „Vyhľadávanie: <query>“ of the core search page.
 */
function SearchTitle({ template, query }: { template: string; query: string }) {
  const { t } = useTranslations();
  const parts = (template || `${t('text211')}: {dotaz}`).split('{dotaz}');

  return (
    <h1 className="nt-h2 text-[#0D110D] [overflow-wrap:anywhere]">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? <span className="text-[#127A26]">{query}</span> : null}
          {part}
        </Fragment>
      ))}
    </h1>
  );
}

/**
 * The search of a query (remounted per query, so no result of the previous one stays on screen):
 * the H1, the printer models / categories / articles of the header search, then the products – or,
 * when no product matches, the „nothing found“ card and the category cards. The page waits for the
 * suggestions and the first products together, so the grid does not jump when the card above it
 * arrives.
 */
export function SearchResults({
  query,
  breadcrumbs,
  breadcrumbsLabel,
  texts,
  listing,
  panel,
  printerBrandIds,
  categories,
}: SearchViewProps & { query: string }) {
  const suggestions = useHeaderSearch(query, printerBrandIds, { products: false });
  const result = useSearchProducts(query);
  const labels = useResultTexts(panel, result.meta?.last.total ?? 0);

  const section = (children: ReactNode) => (
    <SearchSection
      breadcrumbs={breadcrumbs}
      breadcrumbsLabel={breadcrumbsLabel}
      heading={<SearchTitle template={texts.title} query={query} />}
    >
      {children}
    </SearchSection>
  );

  if (result.query.isError && !result.products.length) {
    return section(
      <GenericError error={result.query.error} reset={() => void result.query.refetch()} />,
    );
  }

  // Printer models / categories / articles of the header search (a one-letter query has none).
  const settled = query.length < MIN_QUERY || suggestions.results !== undefined;
  if (!result.meta || !settled) {
    return section(<ProductsSkeleton count={result.perPage} />);
  }

  const { printers = [], categories: matched = [], articles = [] } = suggestions.results ?? {};
  const none = result.meta.last.total === 0;

  return (
    <>
      {section(
        <>
          <SearchSuggestions
            printers={printers}
            categories={matched}
            articles={articles}
            printersTitle={panel.printersTitle}
          />
          {none ? (
            <SearchEmpty
              variant="none"
              texts={texts}
              panel={panel}
              printerBrandIds={printerBrandIds}
            />
          ) : (
            <SearchListing
              search={query}
              result={result}
              texts={listing}
              heading={labels.products}
            />
          )}
        </>,
      )}
      {none ? categories : null}
    </>
  );
}
